import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "../LanguageContext";

const LINK_IDS = ["services", "process", "websites", "pricing", "about", "faq"];

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + id);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-black/60 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-display text-xl md:text-2xl tracking-tight text-white"
        >
          {t.brand.name}<span className="text-gold-gradient"> {t.brand.suffix}</span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {LINK_IDS.map((id) => (
            <button
              key={id}
              data-testid={`nav-${id}`}
              onClick={() => go(id)}
              className="text-sm text-[#c9c9c9] hover:text-white transition-colors duration-300 relative group"
            >
              {t.nav[id]}
              <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-[#d4af37] group-hover:w-full transition-all duration-300" />
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-blog"
            className="text-sm text-[#c9c9c9] hover:text-white transition-colors duration-300 relative group"
          >
            {t.nav.blog}
            <span className="absolute -bottom-1.5 left-0 w-0 h-px bg-[#d4af37] group-hover:w-full transition-all duration-300" />
          </Link>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-medium glass text-white hover:text-[#d4af37] hover:border-[#d4af37]/40 transition-colors duration-300"
            aria-label="Toggle language"
          >
            <Globe size={14} />
            {lang === "en" ? "ES" : "EN"}
          </button>
          <button
            data-testid="nav-cta"
            onClick={() => go("booking")}
            className="hidden md:inline-flex items-center rounded-full px-6 py-2.5 text-sm font-medium bg-[#d4af37] text-[#050505] hover:bg-[#e9d08a] transition-colors duration-300"
          >
            {t.nav.cta}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden backdrop-blur-2xl bg-black/90 border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINK_IDS.map((id) => (
                <button
                  key={id}
                  data-testid={`mobile-nav-${id}`}
                  onClick={() => go(id)}
                  className="text-left text-lg text-[#c9c9c9] hover:text-white py-1"
                >
                  {t.nav[id]}
                </button>
              ))}
              <Link
                to="/blog"
                data-testid="mobile-nav-blog"
                onClick={() => setOpen(false)}
                className="text-left text-lg text-[#c9c9c9] hover:text-white py-1"
              >
                {t.nav.blog}
              </Link>
              <button
                data-testid="mobile-nav-cta"
                onClick={() => go("booking")}
                className="mt-2 rounded-full px-6 py-3 text-sm font-medium bg-[#d4af37] text-[#050505]"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
